/**
 * useApproachExplorerState.ts
 *
 * Manages tree state for the Approach Explorer: expand/collapse, selection,
 * lazy-load cache for sub-approaches, and batch name resolution.
 */

import { useState, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'localization';
import type {
  ApproachTreeNode,
  ApproachExplorerDataSource,
  ResolvedTypeMetadata,
} from 'types/approachExplorer';
import type { ApproachDefinition } from 'types/approach';
import type { ArcTemplate } from 'types/arc';
import { buildApproachTree, ResolvedNames } from '../utils/buildApproachTree';

/** Cached approach data for an already-loaded sub-approach. */
interface CachedApproach {
  name: string;
  description: string | null;
  definition: ApproachDefinition;
  arcTemplate?: ArcTemplate;
  resolvedNames: ResolvedNames;
}

/** Parameters for initializing the explorer state hook. */
export interface UseApproachExplorerStateParams {
  /** The root tree built from the top-level approach. */
  rootTree: ApproachTreeNode;
  /** Data source for fetching sub-approaches and resolving names. */
  dataSource: ApproachExplorerDataSource;
  /** Initial expanded node IDs. */
  defaultExpandedNodeIds?: string[];
  /** Initial selected node ID. */
  defaultSelectedNodeId?: string | null;
  /** Translated category labels for sub-tree building. */
  categoryLabels?: Record<string, string>;
}

/** Return value of the useApproachExplorerState hook. */
export interface UseApproachExplorerStateReturn {
  /** The current tree with lazy-loaded sub-trees merged in. */
  tree: ApproachTreeNode;
  /** Set of currently expanded node IDs. */
  expandedNodeIds: Set<string>;
  /** Currently selected node ID. */
  selectedNodeId: string | null;
  /** Set of node IDs that are currently loading. */
  loadingNodeIds: Set<string>;
  /** Map of node IDs to their error messages. */
  errorNodeIds: Map<string, string>;
  /** Toggle expand/collapse on a node. Triggers lazy-load for sub-approach nodes. */
  toggleNode: (nodeId: string) => void;
  /** Select a node by ID. */
  selectNode: (nodeId: string | null) => void;
  /** Retry loading a failed node. */
  retryNode: (nodeId: string) => void;
  /** Find a node by its ID in the current tree. */
  findNode: (nodeId: string) => ApproachTreeNode | undefined;
}

/** Recursively find a node by ID in the tree. */
function findNodeInTree(
  node: ApproachTreeNode,
  targetId: string
): ApproachTreeNode | undefined {
  if (node.id === targetId) return node;
  if (node.children) {
    for (const child of node.children) {
      const found = findNodeInTree(child, targetId);
      if (found) return found;
    }
  }
  return undefined;
}

/** Recursively replace a node by ID, returning a new tree. */
function replaceNodeInTree(
  node: ApproachTreeNode,
  targetId: string,
  updater: (n: ApproachTreeNode) => ApproachTreeNode
): ApproachTreeNode {
  if (node.id === targetId) return updater(node);
  if (!node.children) return node;

  const updatedChildren = node.children.map((child) =>
    replaceNodeInTree(child, targetId, updater)
  );

  // Only create new object if children actually changed
  if (updatedChildren.every((c, i) => c === node.children![i])) return node;
  return { ...node, children: updatedChildren };
}

/**
 * Collect all type and context system names from a definition
 * so they can be batch-resolved.
 */
function collectNamesToResolve(definition: ApproachDefinition): {
  typeNames: string[];
  contextNames: string[];
} {
  const typeNames = (definition.providedTypes ?? []).map(
    (pt) => pt.schemaSystemName
  );
  const contextNames = (definition.providedContexts ?? []).map(
    (pc) => pc.contextSystemName
  );
  return { typeNames, contextNames };
}

/**
 * Hook for managing the Approach Explorer tree state.
 *
 * Handles expand/collapse, node selection, lazy-loading of sub-approaches
 * (with caching), and batch name resolution via the data source.
 */
export function useApproachExplorerState({
  rootTree,
  dataSource,
  defaultExpandedNodeIds,
  defaultSelectedNodeId,
  categoryLabels,
}: UseApproachExplorerStateParams): UseApproachExplorerStateReturn {
  const { t } = useTranslation();
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(
    () => new Set(defaultExpandedNodeIds ?? [])
  );
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(
    defaultSelectedNodeId ?? null
  );
  const [loadingNodeIds, setLoadingNodeIds] = useState<Set<string>>(
    () => new Set()
  );
  const [errorNodeIds, setErrorNodeIds] = useState<Map<string, string>>(
    () => new Map()
  );
  // Merged tree with lazy-loaded sub-trees
  const [mergedSubTrees, setMergedSubTrees] = useState<
    Map<string, ApproachTreeNode>
  >(() => new Map());

  // Cache of loaded approach data (prevents re-fetching)
  const cacheRef = useRef<Map<string, CachedApproach>>(new Map());

  // Build the current tree by merging lazy-loaded sub-trees into the root tree
  const tree = useMemo(() => {
    if (mergedSubTrees.size === 0) return rootTree;

    let current = rootTree;
    for (const [nodeId, subTree] of mergedSubTrees) {
      current = replaceNodeInTree(current, nodeId, (node) => ({
        ...node,
        children: subTree.children,
        expandable: true,
        loading: false,
        error: undefined,
      }));
    }
    return current;
  }, [rootTree, mergedSubTrees]);

  /** Load a sub-approach and merge its tree into the main tree. */
  const loadSubApproach = useCallback(
    async (nodeId: string) => {
      const node = findNodeInTree(rootTree, nodeId) ??
        (() => {
          // Also search merged sub-trees
          for (const [, subTree] of mergedSubTrees) {
            const found = findNodeInTree(subTree, nodeId);
            if (found) return found;
          }
          return undefined;
        })();

      if (node?.data?.type !== 'sub-approach-ref') return;

      const { approachSystemName, approachVersion } = node.data.reference;

      // Check cache first
      const cached = cacheRef.current.get(nodeId);
      if (cached) {
        const subTree = buildApproachTree(
          {
            systemName: approachSystemName,
            name: cached.name,
            description: cached.description ?? undefined,
            definition: cached.definition,
          },
          cached.arcTemplate,
          cached.resolvedNames,
          nodeId,
          categoryLabels
        );
        setMergedSubTrees((prev) => new Map(prev).set(nodeId, subTree));
        return;
      }

      // Set loading state
      setLoadingNodeIds((prev) => {
        const next = new Set(prev);
        next.add(nodeId);
        return next;
      });
      setErrorNodeIds((prev) => {
        if (!prev.has(nodeId)) return prev;
        const next = new Map(prev);
        next.delete(nodeId);
        return next;
      });

      try {
        const result = await dataSource.fetchApproach(
          approachSystemName,
          approachVersion
        );

        // Batch-resolve type and context names
        const { typeNames, contextNames } = collectNamesToResolve(
          result.definition
        );

        const resolvedNames: ResolvedNames = {};

        const [resolvedTypes, resolvedContexts] = await Promise.all([
          typeNames.length > 0
            ? dataSource.resolveTypes(typeNames)
            : Promise.resolve(new Map<string, ResolvedTypeMetadata>()),
          contextNames.length > 0
            ? dataSource.resolveContexts(contextNames)
            : Promise.resolve(new Map<string, { displayName: string; description?: string }>()),
        ]);

        resolvedNames.types = resolvedTypes;
        resolvedNames.contexts = resolvedContexts;

        // Cache the loaded data
        cacheRef.current.set(nodeId, {
          name: result.name,
          description: result.description,
          definition: result.definition,
          arcTemplate: result.arcTemplate,
          resolvedNames,
        });

        // Build the sub-tree and merge it
        const subTree = buildApproachTree(
          {
            systemName: approachSystemName,
            name: result.name,
            description: result.description ?? undefined,
            definition: result.definition,
          },
          result.arcTemplate,
          resolvedNames,
          nodeId,
          categoryLabels
        );

        setMergedSubTrees((prev) => new Map(prev).set(nodeId, subTree));
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : t('marketplace.approachExplorer.failedToLoad');
        setErrorNodeIds((prev) => new Map(prev).set(nodeId, message));
      } finally {
        setLoadingNodeIds((prev) => {
          const next = new Set(prev);
          next.delete(nodeId);
          return next;
        });
      }
    },
    [rootTree, mergedSubTrees, dataSource, categoryLabels, t]
  );

  const toggleNode = useCallback(
    (nodeId: string) => {
      setExpandedNodeIds((prev) => {
        const next = new Set(prev);
        if (next.has(nodeId)) {
          next.delete(nodeId);
        } else {
          next.add(nodeId);

          // If this is a sub-approach-ref node that hasn't been loaded yet, trigger lazy-load
          const node = findNodeInTree(tree, nodeId);
          if (
            node?.data?.type === 'sub-approach-ref' &&
            !mergedSubTrees.has(nodeId) &&
            !loadingNodeIds.has(nodeId)
          ) {
            loadSubApproach(nodeId);
          }
        }
        return next;
      });
    },
    [tree, mergedSubTrees, loadingNodeIds, loadSubApproach]
  );

  const selectNode = useCallback((nodeId: string | null) => {
    setSelectedNodeId(nodeId);
  }, []);

  const retryNode = useCallback(
    (nodeId: string) => {
      setErrorNodeIds((prev) => {
        const next = new Map(prev);
        next.delete(nodeId);
        return next;
      });
      // Clear cache for this node so it re-fetches
      cacheRef.current.delete(nodeId);
      loadSubApproach(nodeId);
    },
    [loadSubApproach]
  );

  const findNode = useCallback(
    (nodeId: string): ApproachTreeNode | undefined => {
      return findNodeInTree(tree, nodeId);
    },
    [tree]
  );

  return {
    tree,
    expandedNodeIds,
    selectedNodeId,
    loadingNodeIds,
    errorNodeIds,
    toggleNode,
    selectNode,
    retryNode,
    findNode,
  };
}
